import React, { useState } from 'react';
import { Mail, Clock, Send, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const API_URL = `${API_BASE_URL}/api`;

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    service: 'general'
  });

  const [status, setStatus] = useState({
    submitting: false,
    submitted: false,
    error: null
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear error when user starts typing
    if (status.error) {
      setStatus({ ...status, error: null });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name || !formData.email || !formData.message) {
      setStatus({
        ...status,
        error: 'Please fill in all required fields'
      });
      return;
    }

    setStatus({ submitting: true, submitted: false, error: null });

    try {
      const response = await axios.post(`${API_URL}/contact`, formData);

      if (response.data.success) {
        setStatus({
          submitting: false,
          submitted: true,
          error: null
        });

        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          message: '',
          service: 'general'
        });

        // Hide success message after 5 seconds
        setTimeout(() => {
          setStatus({ submitting: false, submitted: false, error: null });
        }, 5000);
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setStatus({
        submitting: false,
        submitted: false,
        error: error.response?.data?.error || 'Failed to send message. Please try again or email us directly at admin@digitaltrent.co.uk'
      });
    }
  };

  return (
    <div id="contact" className="bg-white py-12 md:py-16 px-4">
      {/* Header */}
      <div className="text-center mb-8 md:mb-12 max-w-3xl mx-auto">
        <h2 className="text-2xl md:text-3xl font-bold mb-3 text-slate-900">
          Get In Touch With Us
        </h2>
        <p className="text-sm md:text-base text-slate-600">
          Ready to transform your marketing? Let's discuss how our platform can help your business grow
        </p>
      </div>

      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">

          {/* Left Side - Contact Info */}
          <div className="space-y-4 md:space-y-5">
            <div>
              <h2 className="text-lg md:text-xl font-bold text-slate-900 mb-2">
                Let's Grow Your Business Together
              </h2>
              <p className="text-sm md:text-base text-slate-600 leading-relaxed">
                Whether you're interested in our DIY Starter, Assisted Growth, or Fully Managed VIP plan, we're here to help you succeed.
              </p>
            </div>

            {/* Contact Cards */}
            <div className="space-y-3">
              <div className="bg-white border-2 border-gray-200 rounded-lg p-3 md:p-4 hover:border-blue-500 hover:shadow-md transition-all duration-300">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 md:w-10 md:h-10 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-4 h-4 md:w-5 md:h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Email</h3>
                    <a href="mailto:admin@digitaltrent.co.uk" className="text-sm md:text-base font-semibold text-slate-900 hover:text-blue-600 transition-colors break-all">
                      admin@digitaltrent.co.uk
                    </a>
                    <p className="text-xs text-slate-500 mt-0.5">We'll respond within 24 hours</p>
                  </div>
                </div>
              </div>

              <div className="bg-white border-2 border-gray-200 rounded-lg p-3 md:p-4 hover:border-blue-500 hover:shadow-md transition-all duration-300">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 md:w-10 md:h-10 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-4 h-4 md:w-5 md:h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Response Time</h3>
                    <p className="text-sm md:text-base font-semibold text-slate-900">Under 24 hours</p>
                    <p className="text-xs text-slate-500 mt-0.5">Average reply time</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden">
            {/* Dark Header with Grid Pattern */}
            <div className="relative h-12 md:h-14 overflow-hidden" style={{ backgroundColor: '#0f172a' }}>
              <div className="absolute top-0 left-0 w-24 h-24 pointer-events-none opacity-50">
                <div
                  style={{
                    backgroundImage: `
                      linear-gradient(#2563eb 0.5px, transparent 0.5px),
                      linear-gradient(90deg, #2563eb 0.5px, transparent 0.5px)
                    `,
                    backgroundSize: '8px 8px'
                  }}
                  className="absolute inset-0"
                ></div>
                <div
                  style={{
                    background: 'linear-gradient(135deg, transparent 0%, #0f172a 70%)'
                  }}
                  className="absolute inset-0"
                ></div>
              </div>
              <div className="relative h-full flex items-center px-6">
                <h2 className="text-sm md:text-base font-bold text-white uppercase tracking-wide">
                  Send Us a Message
                </h2>
              </div>
            </div>

            <div className="p-5 md:p-6">
              {/* Success Message */}
              {status.submitted && (
                <div className="mb-4 p-3 bg-green-50 border-2 border-green-500 rounded-lg flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs md:text-sm font-semibold text-green-900 mb-0.5">Message sent successfully!</p>
                    <p className="text-xs text-green-700">We'll get back to you within 24 hours. Check your email for confirmation.</p>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {status.error && (
                <div className="mb-4 p-3 bg-red-50 border-2 border-red-500 rounded-lg flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 md:w-5 md:h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs md:text-sm font-semibold text-red-900 mb-0.5">Failed to send message</p>
                    <p className="text-xs text-red-700">{status.error}</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-3 md:space-y-4">
                {/* Name */}
                <div>
                  <label htmlFor="name" className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Smith"
                    required
                    className="w-full px-3 py-2 md:py-2.5 border-2 border-gray-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all duration-300"
                  />
                </div>

                {/* Email & Phone */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                  <div>
                    <label htmlFor="email" className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@company.co.uk"
                      required
                      className="w-full px-3 py-2 md:py-2.5 border-2 border-gray-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all duration-300"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+44 7700 900000"
                      className="w-full px-3 py-2 md:py-2.5 border-2 border-gray-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all duration-300"
                    />
                  </div>
                </div>

                {/* Company */}
                <div>
                  <label htmlFor="company" className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                    Company Name
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Your Company Ltd"
                    className="w-full px-3 py-2 md:py-2.5 border-2 border-gray-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all duration-300"
                  />
                </div>

                {/* Service */}
                <div>
                  <label htmlFor="service" className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                    What are you interested in? *
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 md:py-2.5 border-2 border-gray-200 rounded-lg text-sm text-slate-900 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all duration-300 cursor-pointer appearance-none bg-white"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 1rem center',
                      paddingRight: '2.5rem'
                    }}
                  >
                    <option value="general">General Enquiry</option>
                    <option value="diy">Marketing Studio (£39/month)</option>
                    <option value="assisted">Assisted Growth (£129/month)</option>
                    <option value="managed">Fully Managed VIP (£449/month)</option>
                    <option value="consultation">Marketing Consultation</option>
                  </select>
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                    Your Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your project and goals..."
                    required
                    rows="4"
                    className="w-full px-3 py-2 md:py-2.5 border-2 border-gray-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all duration-300 resize-vertical"
                  ></textarea>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={status.submitting}
                  className={`w-full py-2.5 md:py-3 rounded-lg text-sm md:text-base font-semibold flex items-center justify-center gap-2 transition-all duration-300 shadow-lg ${
                    status.submitting
                      ? 'bg-slate-400 text-white cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-xl hover:-translate-y-0.5 shadow-blue-600/30'
                  }`}
                >
                  {status.submitting ? (
                    <>
                      <div className="w-4 h-4 md:w-5 md:h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Sending...
                    </>
                  ) : status.submitted ? (
                    <>
                      <CheckCircle className="w-4 h-4 md:w-5 md:h-5" />
                      Message Sent!
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 md:w-5 md:h-5" />
                      Send Message
                    </>
                  )}
                </button>

                <p className="text-center text-xs text-slate-500 mt-3">
                  By submitting this form, you agree to our Privacy Policy and Terms of Service.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
